import { productEligibilityReasonSchema } from "@adpilot/shared";
import { z } from "zod";

export const campaignCreationInvalidProductSchema = z
  .object({
    asin: z.string(),
    sku: z.string().nullable(),
    reason: z.enum([
      "missing_sku",
      "duplicate_sku",
      "not_found",
      "not_active",
      "asin_mismatch",
      "parent",
      "out_of_stock",
      "inventory_unknown",
      "ineligible",
      "not_returned",
    ]),
    message: z.string().optional(),
    eligibilityReasons: z.array(productEligibilityReasonSchema).optional(),
  })
  .strict();

export type CampaignCreationInvalidProduct = z.infer<typeof campaignCreationInvalidProductSchema>;

const invalidProductDetailsSchema = z.object({
  products: z.array(campaignCreationInvalidProductSchema),
});

const INVALID_PRODUCT_MARKER_LIMIT = 100;
const REQUEST_LOCATOR_LIMIT = 50;
const LEGACY_DRAFT_PREFIX = "adpilot:campaign-creation:v1:";
const LEGACY_ACTIVE_DRAFT_PREFIX = "adpilot:campaign-creation:active:v1:";
const SPONSORED_DISPLAY_DRAFT_PREFIX = "adpilot:campaign-creation:sd:v1:";
const volatileRequestShops = new Map<string, string>();

const locatorEntrySchema = z.object({ shopId: z.string(), updatedAt: z.string().datetime() });
const locatorMapSchema = z.record(locatorEntrySchema);

export function parseCampaignCreationInvalidProducts(
  details: unknown,
): CampaignCreationInvalidProduct[] {
  const parsed = invalidProductDetailsSchema.safeParse(details);
  return parsed.success ? parsed.data.products.slice(0, INVALID_PRODUCT_MARKER_LIMIT) : [];
}

export function clearLegacyCampaignCreationState(storage: Storage): void {
  try {
    const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index)).filter(
      (key): key is string =>
        key !== null &&
        (key.startsWith(LEGACY_DRAFT_PREFIX) || key.startsWith(LEGACY_ACTIVE_DRAFT_PREFIX)),
    );
    for (const key of keys) storage.removeItem(key);
  } catch {
    // Legacy cleanup is best-effort and must never block a fresh wizard entry.
  }
}

export function sponsoredDisplayDraftKey(shopId: string): string {
  return `${SPONSORED_DISPLAY_DRAFT_PREFIX}${shopId}`;
}

// The Sponsored Display draft survives navigation on purpose, but a submitted
// campaign must not be rehydrated as a draft on the next wizard entry.
export function clearSponsoredDisplayDraft(storage: Storage, shopId: string): void {
  try {
    storage.removeItem(sponsoredDisplayDraftKey(shopId));
  } catch {
    // Storage can be denied in privacy modes; the draft is then memory-only anyway.
  }
}

function locatorKey(workspaceId: string) {
  return `adpilot:campaign-creation:requests:v1:${workspaceId}`;
}

function volatileRequestKey(workspaceId: string, requestId: string) {
  return `${workspaceId}:${requestId}`;
}

export function rememberCampaignRequestShop(
  storage: Storage,
  workspaceId: string,
  requestId: string,
  shopId: string,
): boolean {
  const memoryKey = volatileRequestKey(workspaceId, requestId);
  volatileRequestShops.delete(memoryKey);
  volatileRequestShops.set(memoryKey, shopId);
  const workspacePrefix = `${workspaceId}:`;
  const workspaceKeys = [...volatileRequestShops.keys()].filter((key) =>
    key.startsWith(workspacePrefix),
  );
  for (const staleKey of workspaceKeys.slice(0, -REQUEST_LOCATOR_LIMIT)) {
    volatileRequestShops.delete(staleKey);
  }
  try {
    const current = locatorMapSchema
      .catch({})
      .parse(JSON.parse(storage.getItem(locatorKey(workspaceId)) ?? "{}"));
    const latestRecordedAt = Math.max(
      0,
      ...Object.values(current).map((entry) => Date.parse(entry.updatedAt) || 0),
    );
    delete current[requestId];
    current[requestId] = {
      shopId,
      // Date alone is only millisecond-granular. Advance monotonically so a burst
      // of locators retains the actual call order after JSON persistence.
      updatedAt: new Date(Math.max(Date.now(), latestRecordedAt + 1)).toISOString(),
    };
    const bounded = Object.fromEntries(
      Object.entries(current)
        .sort(([, left], [, right]) => right.updatedAt.localeCompare(left.updatedAt))
        .slice(0, REQUEST_LOCATOR_LIMIT),
    );
    storage.setItem(locatorKey(workspaceId), JSON.stringify(bounded));
    return true;
  } catch {
    return false;
  }
}

export function locateCampaignRequestShop(
  storage: Storage,
  workspaceId: string,
  requestId: string,
): string | null {
  try {
    const current = locatorMapSchema.safeParse(
      JSON.parse(storage.getItem(locatorKey(workspaceId)) ?? "{}"),
    );
    if (current.success && current.data[requestId]?.shopId) return current.data[requestId].shopId;
    return volatileRequestShops.get(volatileRequestKey(workspaceId, requestId)) ?? null;
  } catch {
    return volatileRequestShops.get(volatileRequestKey(workspaceId, requestId)) ?? null;
  }
}
